import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  IconShield,
  IconUser,
  IconLogout,
  IconLayoutDashboard,
  IconCrown,
  IconMenu2,
  IconX,
  IconSettings
} from '@tabler/icons-react';
import ThemeToggle from './ThemeToggle';
import LogoIcon from './LogoIcon';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Закрытие dropdown при клике вне
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' 
        ? 'text-gray-900 dark:text-white bg-gray-200 dark:bg-dark-850' 
        : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-850';
    }
    return location.pathname.startsWith(path) 
      ? 'text-gray-900 dark:text-white bg-gray-200 dark:bg-dark-850' 
      : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-850';
  };
  
  return (
    <header className="sticky top-0 z-40 border-b border-gray-300 dark:border-dark-850/50 backdrop-blur-xl bg-white/30 dark:bg-dark-950/30 transition-colors">
      <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
        {/* Логотип */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
          <LogoIcon />
          <span className="font-bold text-xl text-gray-900 dark:text-white">XN Connect</span>
        </Link>

        {/* Навигация - скрыта на мобильных */}
        <nav className="header-nav flex-1 items-center justify-center gap-1 mx-4">
          <Link to="/" className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${isActive('/')}`}>Главная</Link>
          <Link to="/dashboard" className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${isActive('/dashboard')}`}>Панель</Link>
          {!window.TunnelBridge && <Link to="/download" className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${isActive('/download')}`}>Скачать</Link>}
          <Link to="/subscribe" className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${isActive('/subscribe')}`}>Подписка</Link>
          {user?.is_admin && (
            <Link to="/admin" className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1 ${isActive('/admin')}`}>
              <IconShield className="w-4 h-4" />
              <span className="hidden sm:inline">Админ</span>
            </Link>
          )}
        </nav>

        {/* Кнопки - скрыты на мобильных */}
        <div className="header-buttons items-center gap-2 sm:gap-3 flex-shrink-0">
          <ThemeToggle />
          {isAuthenticated ? (
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-10 h-10 rounded-full bg-primary-500 hover:bg-primary-600 flex items-center justify-center text-white font-bold text-sm transition-colors"
              >
                {user?.username?.[0]?.toUpperCase() || 'U'}
              </button>
              
              {/* Dropdown меню */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-dark-900 rounded-xl shadow-lg border border-gray-200 dark:border-dark-800 py-2 z-50">
                  {/* Инфо пользователя */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/20 to-primary-500/5 flex items-center justify-center">
                        <span className="text-primary-500 font-bold">{user?.username?.[0]?.toUpperCase()}</span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{user?.username}</div>
                        <div className="text-xs text-gray-500 dark:text-dark-500 flex items-center gap-1">
                          {user?.plan === 'paid' ? (
                            <><IconCrown className="w-3 h-3 text-yellow-500" /> PRO</>
                          ) : 'Free'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Ссылки */}
                  <div className="py-1">
                    <Link 
                      to="/dashboard" 
                      className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <IconLayoutDashboard className="w-4 h-4" />
                      Панель управления
                    </Link>
                    <Link 
                      to="/settings" 
                      className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <IconSettings className="w-4 h-4" />
                      Настройки
                    </Link>
                    {user?.is_admin && (
                      <Link 
                        to="/admin" 
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <IconShield className="w-4 h-4" />
                        Админ-панель
                      </Link>
                    )}
                  </div>
                  
                  {/* Выход */}
                  <div className="border-t border-gray-200 dark:border-dark-800 pt-1">
                    <button 
                      onClick={() => { logout(); setDropdownOpen(false); }}
                      className="flex items-center gap-3 w-full px-4 py-2 text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      <IconLogout className="w-4 h-4" />
                      Выйти
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="px-2 sm:px-4 py-2 text-gray-700 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white text-xs sm:text-sm font-medium transition-colors whitespace-nowrap">Войти</Link>
              <Link to="/register" className="px-3 sm:px-4 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg font-medium text-white text-xs sm:text-sm transition-colors whitespace-nowrap">
                Начать
              </Link>
            </>
          )}
        </div>

        {/* Мобильная кнопка меню и ThemeToggle */}
        <div className="header-mobile-menu items-center gap-2">
          <ThemeToggle />
          <button 
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-850 rounded-md transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <IconX className="w-6 h-6 text-gray-900 dark:text-white" />
            ) : (
              <IconMenu2 className="w-6 h-6 text-gray-900 dark:text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Мобильное меню */}
      {mobileMenuOpen && (
        <div className="header-mobile-dropdown border-t border-gray-200 dark:border-dark-850/50 bg-white/95 dark:bg-dark-950/95 backdrop-blur-xl">
          <div className="px-4 py-4 space-y-2">
            <Link 
              to="/" 
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActive('/')}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Главная
            </Link>
            <Link 
              to="/dashboard" 
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActive('/dashboard')}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Панель управления
            </Link>
            {!window.TunnelBridge && <Link 
              to="/download" 
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActive('/download')}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Скачать
            </Link>}
            <Link 
              to="/subscribe" 
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActive('/subscribe')}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Подписка
            </Link>
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isActive('/admin')}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <IconShield className="w-4 h-4" />
                Админ-панель
              </Link>
            )}
            
            {/* Разделитель и кнопки авторизации */}
            <div className="pt-2 mt-2 border-t border-gray-200 dark:border-dark-800 space-y-2">
              {isAuthenticated ? (
                <>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold">
                      {user?.username?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{user?.username}</div>
                      <div className="text-xs text-gray-500 dark:text-dark-500 flex items-center gap-1">
                        {user?.plan === 'paid' ? (
                          <><IconCrown className="w-3 h-3 text-yellow-500" /> PRO</>
                        ) : 'Free'}
                      </div>
                    </div>
                  </div>
                  <Link 
                    to="/settings" 
                    className="flex items-center gap-3 w-full px-4 py-3 text-gray-700 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <IconSettings className="w-4 h-4" />
                    Настройки
                  </Link>
                  <button 
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <IconLogout className="w-4 h-4" />
                    Выйти
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="block text-center px-4 py-3 text-gray-700 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white text-sm font-medium transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-dark-850"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Войти
                  </Link>
                  <Link 
                    to="/register" 
                    className="block text-center px-4 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-medium text-white text-sm transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Начать бесплатно
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
